<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue';
import { useApp } from '../stores/app';
import { live, ui } from '../editor/bridge';
import { keepCanvasSelection } from '../editor/dismiss';
import { t } from '../i18n';
import { settings } from '../platform';
import Icon from './Icon.vue';
import CanvasSettings from './CanvasSettings.vue';
import { modulePanels } from '../modules';
import { ICON_NAMES } from './Icon.vue';

/** Top-right pill: history, module panels, search, object tree, canvas settings, diagnostics. */
const settingsOpen = ref(false);

/** At most this many module buttons stand in the pill; the others are in the "…" menu. */
const MAX_PANEL_BUTTONS = 3;
type PanelInfo = ReturnType<typeof modulePanels>[number];

/** Module panels in the user's order (settings.panelButtonOrder), new modules at the end. */
const ordered = computed<PanelInfo[]>(() => {
  const all = modulePanels();
  const rank = (id: string) => {
    const i = settings.panelButtonOrder.indexOf(id);
    return i < 0 ? Infinity : i;
  };
  return [...all].sort((a, b) => rank(a.moduleId) - rank(b.moduleId));
});
const shown = computed(() => ordered.value.filter((p) => !settings.hiddenPanelButtons.includes(p.moduleId)).slice(0, MAX_PANEL_BUTTONS));
/** The menu is there when some panel has no button (hidden or over the limit) or there is an order to change. */
const hasMenu = computed(() => ordered.value.length > shown.value.length || ordered.value.length > 1);

const panelOpen = computed(() => ui.sidebar?.moduleId ?? null);
function togglePanel(id: string): void {
  ui.sidebar = panelOpen.value === id ? null : { kind: 'panel', moduleId: id };
}
const app = useApp();
function hideButton(id: string): void {
  settings.hiddenPanelButtons = [...settings.hiddenPanelButtons.filter((x) => x !== id), id];
  app.notify('info', t('sidebar.buttonHidden'));
}
function setShown(id: string, on: boolean): void {
  const rest = settings.hiddenPanelButtons.filter((x) => x !== id);
  settings.hiddenPanelButtons = on ? rest : [...rest, id];
}
/** Move a module one place up or down in the order of the buttons. */
function move(id: string, by: -1 | 1): void {
  const ids = ordered.value.map((p) => p.moduleId);
  const i = ids.indexOf(id);
  const j = i + by;
  if (i < 0 || j < 0 || j >= ids.length) return;
  [ids[i], ids[j]] = [ids[j], ids[i]];
  settings.panelButtonOrder = ids;
}
const isShown = (id: string) => shown.value.some((p) => p.moduleId === id);
const isHidden = (id: string) => settings.hiddenPanelButtons.includes(id);

const menuOpen = ref(false);
const menuEl = ref<HTMLElement | null>(null);
function onOutside(e: PointerEvent): void {
  if (!menuOpen.value || !menuEl.value || menuEl.value.contains(e.target as Node) || (e.target as HTMLElement).closest?.('[data-act="panels-menu"]')) return;
  menuOpen.value = false;
  keepCanvasSelection(e);
}
window.addEventListener('pointerdown', onOutside, true);
onBeforeUnmount(() => window.removeEventListener('pointerdown', onOutside, true));

const svgUrl = (svg: string) => 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
</script>

<template>
  <div class="top-right">
  <div class="float pillbar glass">
    <button class="icon-btn" :disabled="!ui.canUndo || ui.readOnly" :aria-label="t('cmd.undo')" :title="t('cmd.undo')" @click="live.editor?.undo()"><Icon name="undo" /></button>
    <button class="icon-btn" :disabled="!ui.canRedo || ui.readOnly" :aria-label="t('cmd.redo')" :title="t('cmd.redo')" @click="live.editor?.redo()"><Icon name="redo" /></button>
    <span class="sep"></span>
    <template v-if="ordered.length">
      <button
        v-for="p in shown"
        :key="p.moduleId"
        class="icon-btn"
        :class="{ active: panelOpen === p.moduleId }"
        :aria-pressed="panelOpen === p.moduleId"
        :aria-label="p.title"
        :title="p.title + ' — ' + t('sidebar.rightClickHide')"
        :data-panel-btn="p.moduleId"
        @click="togglePanel(p.moduleId)"
        @contextmenu.prevent="hideButton(p.moduleId)"
      >
        <img v-if="p.icon && !ICON_NAMES.has(p.icon)" class="mod-icon" :src="svgUrl(p.icon)" alt="" />
        <Icon v-else :name="p.icon ?? 'module'" />
      </button>
      <button
        v-if="hasMenu"
        class="icon-btn more"
        :class="{ active: menuOpen }"
        :aria-expanded="menuOpen"
        :aria-label="t('sidebar.panelsMenu')"
        :title="t('sidebar.panelsMenu')"
        data-act="panels-menu"
        @click="menuOpen = !menuOpen"
      >
        <Icon name="chevronDown" :size="14" />
      </button>
      <span class="sep"></span>
    </template>
    <button class="icon-btn" :class="{ active: ui.searchOpen }" :aria-label="t('search.title')" :title="t('search.title') + ' (Ctrl+F)'" @click="ui.searchOpen = !ui.searchOpen"><Icon name="search" /></button>
    <button class="icon-btn" :class="{ active: settings.treeOpen }" :aria-pressed="settings.treeOpen" :aria-label="t('tree.title')" :title="t('tree.title')" @click="settings.treeOpen = !settings.treeOpen"><Icon name="tree" /></button>
    <button class="icon-btn" :class="{ active: settingsOpen }" :aria-expanded="settingsOpen" :aria-label="t('props.canvas')" :title="t('props.canvas')" @click="(settingsOpen = !settingsOpen), settingsOpen && (ui.chatOpen = false)"><Icon name="settings" /></button>
    <button class="icon-btn" :aria-label="t('keys.title')" :title="t('keys.title') + ' (?)'" @click="ui.keysOpen = true"><Icon name="keyboard" /></button>
    <button class="icon-btn" :class="{ active: settings.perfPanel }" :aria-pressed="settings.perfPanel" :aria-label="t('settings.perfPanel')" :title="t('settings.perfPanel')" @click="settings.perfPanel = !settings.perfPanel"><Icon name="perf" /></button>
  </div>
  <button
    type="button"
    class="chat-launch glass"
    :class="{ active: ui.chatOpen }"
    :aria-pressed="ui.chatOpen"
    :aria-expanded="ui.chatOpen"
    :aria-label="t('feed.open')"
    :title="t('feed.open')"
    data-act="feed"
    @click="(ui.chatOpen = !ui.chatOpen), ui.chatOpen && (settingsOpen = false)"
  >
    <Icon name="menu" />
  </button>

  <!-- module panels: order, which of them have a button (at most three), open the rest from here -->
  <div v-if="menuOpen" ref="menuEl" class="float card panels-menu" role="menu" :aria-label="t('sidebar.panelsMenu')" data-vp-dismiss @pointerdown.stop>
    <p class="menu-title">{{ t('sidebar.panelsMenu') }}</p>
    <p class="menu-hint">{{ t('sidebar.panelsLimit', { n: MAX_PANEL_BUTTONS }) }}</p>
    <ul>
      <li v-for="(p, i) in ordered" :key="p.moduleId" :data-panel-item="p.moduleId">
        <button class="open" :title="p.title" @click="togglePanel(p.moduleId), (menuOpen = false)">
          <img v-if="p.icon && !ICON_NAMES.has(p.icon)" class="mod-icon" :src="svgUrl(p.icon)" alt="" />
          <Icon v-else :name="p.icon ?? 'module'" :size="16" />
          <span class="name">{{ p.title }}</span>
          <span v-if="!isHidden(p.moduleId) && !isShown(p.moduleId)" class="badge">{{ t('sidebar.overLimit') }}</span>
        </button>
        <button class="icon-btn small" :disabled="i === 0" :aria-label="t('sidebar.moveUp')" :title="t('sidebar.moveUp')" data-act="up" @click="move(p.moduleId, -1)"><Icon name="chevronDown" :size="14" class="up" /></button>
        <button class="icon-btn small" :disabled="i === ordered.length - 1" :aria-label="t('sidebar.moveDown')" :title="t('sidebar.moveDown')" data-act="down" @click="move(p.moduleId, 1)"><Icon name="chevronDown" :size="14" /></button>
        <button
          class="icon-btn small"
          :class="{ active: !isHidden(p.moduleId) }"
          :aria-pressed="!isHidden(p.moduleId)"
          :aria-label="t('sidebar.showButton')"
          :title="t('sidebar.showButton')"
          data-act="shown"
          @click="setShown(p.moduleId, isHidden(p.moduleId))"
        >
          <Icon name="check" :size="14" />
        </button>
      </li>
    </ul>
  </div>
  </div>
  <CanvasSettings v-if="settingsOpen" @close="settingsOpen = false" />
</template>

<style scoped>
.top-right {
  position: absolute;
  top: 12px;
  right: 20px;
  z-index: 25;
  display: flex;
  align-items: center;
  gap: 10px;
}
.top-right > .pillbar {
  position: relative;
}
.chat-launch {
  position: relative;
  width: 42px;
  height: 42px;
  padding: 0;
  border-radius: 50%;
  border: 1px solid var(--float-border);
  box-shadow: var(--float-shadow);
  color: var(--text-2);
  cursor: pointer;
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.chat-launch:hover {
  color: var(--text);
}
.chat-launch.active {
  color: var(--accent-text);
  box-shadow: var(--float-shadow), 0 0 0 2px var(--accent);
}
.mod-icon {
  width: 18px;
  height: 18px;
}
.icon-btn.more {
  width: 24px;
}
.panels-menu {
  top: calc(100% + 10px);
  right: 52px;
  width: 300px;
  padding: 10px 8px 8px;
  z-index: 26;
}
.menu-title {
  margin: 0 8px 2px;
  font-size: 13px;
  font-weight: 600;
}
.menu-hint {
  margin: 0 8px 6px;
  font-size: 11.5px;
  color: var(--text-3);
}
ul {
  list-style: none;
  margin: 0;
  padding: 0;
}
li {
  display: flex;
  align-items: center;
  gap: 2px;
}
.open {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  height: 34px;
  padding: 0 8px;
  border: none;
  border-radius: var(--radius-panel);
  background: transparent;
  color: var(--text);
  font: inherit;
  font-size: 13px;
  cursor: pointer;
  text-align: left;
}
.open:hover {
  background: var(--hover);
}
.name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.badge {
  flex: none;
  font-size: 10.5px;
  padding: 1px 6px;
  border-radius: var(--radius-control);
  background: var(--hover);
  color: var(--text-3);
}
.icon-btn.small {
  width: 28px;
  height: 28px;
}
.up {
  transform: rotate(180deg);
}
</style>
